import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {
    SearchSnapshotsRequest,
    SearchSnapshotsResult,
    SnapshotStatisticsInterval,
    SnapshotStatisticsResult,
} from "../payload-types/snapshot-search";

/**
 * API interface for the cross-valuation snapshot listing (#19113), newest first. With
 * {@code allUsers=false} (POST {@link RevaApi.PATH_SNAPSHOTS_SEARCH}) the result is restricted to
 * the requesting user's own snapshots (own "Historie"/DONE tab); with {@code allUsers=true}
 * (POST {@link RevaApi.PATH_SNAPSHOTS_ADMIN_SEARCH}) it lists all users' snapshots (Admin tab) and
 * requires the reva-ownership-admin permission.
 */
export class SearchSnapshotsApiInterface extends SimpleApiInterface<SearchSnapshotsResult> {
    /**
     * @param request Paging + der globale Freitextfilter
     * @param allUsers false = eigene Snapshots (DONE-Tab), true = die aller User (Admin-Tab, reva-ownership-admin)
     * @param signal optionales AbortSignal, damit ein neuerer Tastendruck diese laufende Suche abbrechen kann
     */
    constructor(
        private readonly request: SearchSnapshotsRequest,
        private readonly allUsers = false,
        private readonly signal?: AbortSignal,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([
            this.allUsers ? RevaApi.PATH_SNAPSHOTS_ADMIN_SEARCH : RevaApi.PATH_SNAPSHOTS_SEARCH,
        ]);
    }

    protected buildPayload(): Record<string, unknown> {
        return {
            pageIndex: this.request.pageIndex,
            pageSize: this.request.pageSize,
            filterFreeText: this.request.filterFreeText ?? undefined,
        };
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<SearchSnapshotsResult> {
        return executor.fetchPOST(url, credentials, this.buildPayload(), this.signal);
    }
}

/**
 * API interface for the snapshot statistics (#19113): snapshot counts per calendar bucket
 * (week/month/year) and snapshot type, for the last `periods` buckets (gap-free, oldest first).
 * With {@code allUsers=false} (GET {@link RevaApi.PATH_SNAPSHOTS_STATISTICS}) over the requesting
 * user's own snapshots; with {@code allUsers=true} (GET {@link RevaApi.PATH_SNAPSHOTS_ADMIN_STATISTICS})
 * over all users' snapshots (Admin tab, requires reva-ownership-admin).
 */
export class SnapshotStatisticsApiInterface extends SimpleApiInterface<SnapshotStatisticsResult> {
    constructor(
        private readonly interval: SnapshotStatisticsInterval,
        private readonly periods: number,
        private readonly allUsers = false,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl(
            [this.allUsers ? RevaApi.PATH_SNAPSHOTS_ADMIN_STATISTICS : RevaApi.PATH_SNAPSHOTS_STATISTICS],
            {interval: this.interval, periods: this.periods},
        );
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<SnapshotStatisticsResult> {
        return executor.fetchGET(url, credentials);
    }
}
